// Transform helpers — ported from eb-ui creator-collections-listing
// `src/_shared/transform.tsx` (identical logic).
//
// `@eventbrite/event-renderer` `FormattedEvent` → the LOCAL `CollectionEventCard`
// (the card shape this app's vertical event card + carousels consume). The
// `transformDestinationEvent` upgrade (origin → destination FormattedEvent) is
// sourced from the sanctioned `@eventbrite/consumer-search.event-renderer` and is
// applied in the destination BFF, not here. `date-fns` / `date-fns-tz` replace
// `@eventbrite/legacy-infra.datetime-fns` (harness ghost rule).
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import type {
    CollectionCardOriginType,
    CollectionEventCard,
    EventOriginType,
} from '@/types';

const getLocationInfo = (venue?: {
    name: string;
    address?: { localized_area_display: string };
}): string => {
    if (venue?.name && venue?.address?.localized_area_display) {
        return `${venue.name} \u2022 ${venue.address.localized_area_display}`;
    }
    return venue?.name || venue?.address?.localized_area_display || '';
};

export const transformCollectionEvent = (event: EventOriginType): CollectionEventCard => {
    const [startDate, startTime] =
        event?.series?.next_dates?.[0]?.start.split('T') || event.start.local.split('T');
    return {
        id: event.id,
        imageUrl: event?.logo?.url || '',
        isFree: event.is_free,
        isRepeatingEvent: event.is_series_parent,
        isOnlineEvent: event.online_event,
        locationInfo: getLocationInfo(event?.venue),
        name: event?.name.text || '',
        savedByYou: !!event.saves?.saved_by_you,
        startDate: event.hide_start_date ? undefined : startDate,
        startTime,
        timezone: event.start.timezone,
        url: event.url,
        isPrivate: !event.listed,
        isRestricted: !!event.is_protected_event,
        eventSalesStatus: {
            salesStatus: event?.event_sales_status?.sales_status || 'on_sale',
            message: event?.event_sales_status?.message || '',
            messageType: event?.event_sales_status?.message_type || '',
            messageCode: event?.event_sales_status?.message_code || '',
        },
        isSoldOut:
            event?.event_sales_status?.sales_status === 'sold_out' && !event?.series_id,
        edgeColor: event?.image?.edge_color || undefined,
        formattedPriceString: event.is_free ? '' : event.price_range,
        minPrice: {
            currency: event?.ticket_availability?.minimum_ticket_price?.currency || '',
            currencyFormat: '¤#,##0.00',
            minPriceValue: event?.ticket_availability?.minimum_ticket_price?.value || 0,
        },
        subtitle: event.subtitle,
        ticketsBy: event.tickets_by,
        organizerId: event.organizer_id,
        organizerName: event?.primary_organizer?.name || '',
        organizerFollowerCount: event?.primary_organizer?.num_followers || undefined,
    };
};

/** The card model a carousel slide passes to the CreatorCollectionCard. */
export interface CreatorCollectionCardCarouselProps {
    id: string;
    slug: string;
    name: string;
    summary?: string;
    imageUrl?: string;
    numUpcomingEvents?: number;
    organizerId?: string;
}

export const transformCollectionCard = (
    collection: CollectionCardOriginType,
): CreatorCollectionCardCarouselProps => {
    const { id, relative_url, name, summary, image, event_count, organizer_id } = collection;
    return {
        id,
        slug: relative_url,
        name,
        summary,
        imageUrl: image?.url,
        numUpcomingEvents: event_count?.current_future_published,
        organizerId: organizer_id,
    };
};

/**
 * Sorts events by their startDate + startTime.
 * Handles series events where startDate reflects the next upcoming occurrence.
 */
export const sortEventsByStartDate = (
    events: CollectionEventCard[],
    ascending: boolean,
): CollectionEventCard[] => {
    return [...events].sort((a, b) => {
        const dateA = a.startDate ? `${a.startDate}T${a.startTime}` : '';
        const dateB = b.startDate ? `${b.startDate}T${b.startTime}` : '';
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return ascending ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
    });
};

/**
 * Adjusts the start time of repeating events from UTC to the event's local timezone.
 * Non-repeating events or events without UTC times are returned unchanged.
 */
export const adjustRepeatingEventTimes = (
    events: CollectionEventCard[],
): CollectionEventCard[] => {
    return events.map((event) => {
        if (!event.isRepeatingEvent) {
            return event;
        }
        const utcTime = event.startTime;
        if (utcTime && utcTime.endsWith('Z')) {
            const date = new Date(`${event.startDate}T${utcTime}`);
            const localDate = utcToZonedTime(date, event.timezone || 'UTC');
            const localTime = format(localDate, 'HH:mm');
            const localDateStr = format(localDate, 'yyyy-MM-dd');
            return { ...event, startTime: localTime, startDate: localDateStr };
        }
        return event;
    });
};
